import { isPlatformCountry } from '../config/countries';

/**
 * Normalize Google Places Autocomplete data into V2 location model
 * with automatic V1 legacy field synchronization
 * 
 * Handles special cases:
 * - Paris arrondissements: "Paris 13e" format
 * - France departments: extracted from postal code
 * - Belgium/Switzerland: city only, no department
 * 
 * @param {google.maps.places.PlaceResult} place - Google Places result object
 * @returns {Object} Normalized location data with V2 and V1 fields
 */
export function normalizeLocationFromAutocomplete(place) {
  if (!place || !place.address_components) {
    return {
      country: null,
      city: null,
      department: null,
      mainCity: null,
      cities: null,
    };
  }

  const components = place.address_components;

  // Extract country code (ISO 2 lettres, ex: FR, BE, CH)
  const countryComponent = components.find((comp) => comp.types.includes('country'));
  const countryCode = countryComponent?.short_name?.toUpperCase() || null;
  const validCountry = isPlatformCountry(countryCode) ? countryCode : null;

  // Extract administrative_area_level_2 (for Paris detection and department)
  const adminLevel2 = components.find((comp) => comp.types.includes('administrative_area_level_2'));
  const adminLevel2Name = adminLevel2?.long_name || '';
  const adminLevel2Short = adminLevel2?.short_name || '';

  // Extract postal code (for department extraction)
  const postalCodeComponent = components.find((comp) => comp.types.includes('postal_code'));
  const postalCode = postalCodeComponent?.long_name || '';

  // Extract locality (city name)
  const localityComponent = components.find((comp) =>
    comp.types.some((type) => ['locality', 'postal_town'].includes(type))
  );
  const locality = localityComponent?.long_name || '';

  // Extract arrondissement (sublocality_level_1)
  const arrondissementComponent = components.find((comp) =>
    comp.types.includes('sublocality_level_1')
  );
  const arrondissementName = arrondissementComponent?.long_name || '';

  // ===== PARIS CASE (Special Logic) =====
  // Improved detection: check if locality starts with "Paris" or contains "Paris" with arrondissement
  const localityStartsWithParis = locality.toLowerCase().startsWith('paris');
  const isParis = adminLevel2Name === 'Paris' || 
                  adminLevel2Short === 'Paris' ||
                  locality === 'Paris' ||
                  localityStartsWithParis;

  if (isParis && validCountry === 'FR') {
    // Extract arrondissement number - try multiple sources
    let arrondissementNumber = null;
    
    // 1. Try from sublocality_level_1 first
    if (arrondissementName) {
      const arrondissementMatch = arrondissementName.match(/\b(\d{1,2})\b/);
      if (arrondissementMatch) {
        arrondissementNumber = arrondissementMatch[1];
      }
    }
    
    // 2. If not found, try from locality (e.g., "Paris 13", "Paris 20e")
    if (!arrondissementNumber && localityStartsWithParis) {
      const localityMatch = locality.match(/paris\s+(\d{1,2})/i);
      if (localityMatch) {
        arrondissementNumber = localityMatch[1];
      }
    }
    
    // 3. Fallback: extract from postal code (75013 -> 13)
    if (!arrondissementNumber && postalCode) {
      const postalMatch = postalCode.match(/75(\d{3})/);
      if (postalMatch) {
        arrondissementNumber = parseInt(postalMatch[1], 10).toString();
      }
    }
    
    // 4. Last fallback: try from place.name
    if (!arrondissementNumber && place.name) {
      const nameMatch = place.name.match(/paris\s+(\d{1,2})/i);
      if (nameMatch) {
        arrondissementNumber = nameMatch[1];
      }
    }

    // Normalize city to canonical format: "Paris 13e"
    let normalizedCity = 'Paris';
    if (arrondissementNumber) {
      normalizedCity = `Paris ${arrondissementNumber}e`;
    }

    // Clean up: remove "Arrondissement", duplicates, trim
    normalizedCity = normalizedCity
      .replace(/arrondissement/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    return {
      country: 'FR',
      city: normalizedCity,
      department: '75',
      // V1 legacy fields (auto-synced)
      mainCity: normalizedCity,
      cities: [normalizedCity],
    };
  }

  // ===== NON-PARIS FRANCE CASE =====
  if (validCountry === 'FR') {
    // Use locality as clean city name
    let cleanCity = locality || place.name || '';
    
    // Clean up: remove "Arrondissement", duplicates, trim
    cleanCity = cleanCity
      .replace(/arrondissement/gi, '')
      .replace(/\b(\d+)\s*\1\b/gi, '$1') // Remove duplicated numbers
      .replace(/\s+/g, ' ')
      .trim();

    // Extract department from postal code (first 2 digits)
    let department = null;
    if (postalCode) {
      const deptMatch = postalCode.match(/^(\d{2})/);
      if (deptMatch) {
        department = deptMatch[1];
      }
    } else if (adminLevel2Short) {
      // Fallback: try to extract from administrative_area_level_2 short name
      const deptMatch = adminLevel2Short.match(/^(\d{2})/);
      if (deptMatch) {
        department = deptMatch[1];
      }
    }

    return {
      country: 'FR',
      city: cleanCity || null,
      department: department || null,
      // V1 legacy fields (auto-synced)
      mainCity: cleanCity || null,
      cities: cleanCity ? [cleanCity] : null,
    };
  }

  // ===== BELGIUM / SWITZERLAND CASES =====
  if (validCountry === 'BE' || validCountry === 'CH') {
    // Use locality as clean city name
    let cleanCity = locality || place.name || '';
    
    // Clean up: remove "Arrondissement", duplicates, trim
    cleanCity = cleanCity
      .replace(/arrondissement/gi, '')
      .replace(/\b(\d+)\s*\1\b/gi, '$1') // Remove duplicated numbers
      .replace(/\s+/g, ' ')
      .trim();

    return {
      country: validCountry,
      city: cleanCity || null,
      department: null, // No department for BE/CH
      // V1 legacy fields (auto-synced)
      mainCity: cleanCity || null,
      cities: cleanCity ? [cleanCity] : null,
    };
  }

  // ===== FALLBACK (Unknown country or invalid data) =====
  const fallbackCity = locality || place.name || '';
  const cleanedCity = fallbackCity
    .replace(/arrondissement/gi, '')
    .replace(/\b(\d+)\s*\1\b/gi, '$1')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    country: validCountry,
    city: cleanedCity || null,
    department: null,
    // V1 legacy fields (auto-synced)
    mainCity: cleanedCity || null,
    cities: cleanedCity ? [cleanedCity] : null,
  };
}

